import { z } from "zod";
import { IsoTimestampSchema, UuidSchema, paginatedResponse } from "./common.js";
export const PlatformSchema = z.enum([
    "instagram",
    "facebook",
    "linkedin",
    "twitter",
    "blog",
]);
export const PostStatusSchema = z.enum([
    "draft",
    "scheduled",
    "publishing",
    "published",
    "failed",
]);
export const BrandVoiceResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    name: z.string(),
    tone: z.string(),
    sample_posts: z.array(z.string()),
    custom_instructions: z.string().nullable(),
    is_default: z.boolean(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const CreateBrandVoiceRequestSchema = z.object({
    name: z.string().min(1).max(120),
    tone: z.string().min(1).max(80),
    sample_posts: z.array(z.string().max(2_000)).max(10).default([]),
    custom_instructions: z.string().max(5_000).optional(),
    is_default: z.boolean().default(false),
});
export const UpdateBrandVoiceRequestSchema = CreateBrandVoiceRequestSchema.partial().refine((v) => Object.keys(v).length > 0, { message: "At least one field required" });
export const ContentPostResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    brand_voice_id: UuidSchema.nullable(),
    platform: PlatformSchema,
    status: PostStatusSchema,
    body: z.string(),
    media_urls: z.array(z.string()),
    utm_source: z.string().nullable(),
    utm_medium: z.string().nullable(),
    utm_campaign: z.string().nullable(),
    utm_content: z.string().nullable(),
    publish_at: IsoTimestampSchema.nullable(),
    published_at: IsoTimestampSchema.nullable(),
    platform_post_id: z.string().nullable(),
    generation_prompt: z.string().nullable(),
    generation_cost_micros: z.number().int(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const CreateContentPostRequestSchema = z.object({
    platform: PlatformSchema,
    body: z.string().min(1).max(10_000),
    brand_voice_id: UuidSchema.optional(),
    media_urls: z.array(z.string()).max(10).default([]),
    utm_source: z.string().max(100).optional(),
    utm_medium: z.string().max(100).optional(),
    utm_campaign: z.string().max(200).optional(),
    utm_content: z.string().max(200).optional(),
    publish_at: IsoTimestampSchema.optional(),
});
export const GeneratePostRequestSchema = z.object({
    platform: PlatformSchema,
    brand_voice_id: UuidSchema.optional(),
    topic: z.string().min(1).max(1_000),
    additional_instructions: z.string().max(10_000).optional(),
});
export const UpdateContentPostRequestSchema = z
    .object({
    body: z.string().min(1).max(10_000).optional(),
    media_urls: z.array(z.string()).max(10).optional(),
    utm_source: z.string().max(100).nullable().optional(),
    utm_medium: z.string().max(100).nullable().optional(),
    utm_campaign: z.string().max(200).nullable().optional(),
    utm_content: z.string().max(200).nullable().optional(),
    publish_at: IsoTimestampSchema.nullable().optional(),
    brand_voice_id: UuidSchema.nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field required" });
export const SchedulePostRequestSchema = z.object({
    publish_at: IsoTimestampSchema,
});
export const PlatformConnectionResponseSchema = z.object({
    id: UuidSchema,
    tenant_id: UuidSchema,
    platform: PlatformSchema,
    display_name: z.string().nullable(),
    status: z.enum(["connected", "expired", "revoked"]),
    token_expires_at: IsoTimestampSchema.nullable(),
    created_at: IsoTimestampSchema,
    updated_at: IsoTimestampSchema,
});
export const UpsertPlatformConnectionRequestSchema = z.object({
    display_name: z.string().max(200).optional(),
    access_token_secret_ref: z.string().max(200).optional(),
    refresh_token_secret_ref: z.string().max(200).optional(),
    token_expires_at: IsoTimestampSchema.optional(),
});
export const ContentPostListResponseSchema = paginatedResponse(ContentPostResponseSchema);
export const BrandVoiceListResponseSchema = z.object({ items: z.array(BrandVoiceResponseSchema) });
export const PlatformConnectionListResponseSchema = z.object({
    items: z.array(PlatformConnectionResponseSchema),
});
// ─── Content analytics (C4) ──────────────────────────────────────────────────
export const ContentAnalyticsItemSchema = z.object({
    post_id: UuidSchema,
    platform: PlatformSchema,
    status: PostStatusSchema,
    body_preview: z.string(),
    clicks: z.number().int(),
    leads: z.number().int(),
    bookings: z.number().int(),
    published_at: IsoTimestampSchema.nullable(),
    created_at: IsoTimestampSchema,
});
export const ContentAnalyticsResponseSchema = z.object({
    items: z.array(ContentAnalyticsItemSchema),
    since: IsoTimestampSchema,
});
// ─── Attribution funnel (F1) ─────────────────────────────────────────────────
export const FunnelPostItemSchema = z.object({
    post_id: UuidSchema,
    platform: PlatformSchema,
    body_preview: z.string(),
    published_at: IsoTimestampSchema.nullable(),
    clicks: z.number().int(),
    leads: z.number().int(),
    booked: z.number().int(),
    closed: z.number().int(),
});
// ─── Content image upload (R2) ────────────────────────────────────────────────
export const ContentImageUploadResponseSchema = z.object({
    /** Public URL of the uploaded object (browser- and platform-API-readable). */
    url: z.string().url(),
    /** Object key inside the bucket (e.g. `<tenant_id>/<uuid>.<ext>`). */
    key: z.string(),
    /** Detected mime type (echoed back for client-side rendering decisions). */
    mime_type: z.string(),
    /** Size in bytes, post-upload. */
    size_bytes: z.number().int().min(0),
});
export const FunnelResponseSchema = z.object({
    since: IsoTimestampSchema,
    pipeline_summary: z.record(z.string(), z.number().int()),
    by_post: z.array(FunnelPostItemSchema),
});
//# sourceMappingURL=content.js.map